import React, { useEffect, useRef } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { Alert, Spinner } from 'react-bootstrap';
import { formatDuration } from '../utils/formatters.js';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '8px'
};

export default function TrackingMap({ trackings }) {
  const mapRef = useRef(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY
  });

  useEffect(() => {
    if (!isLoaded || !mapRef.current || trackings.length === 0) return;

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 14,
      center: { lat: -37.78, lng: 174.77 } // Auckland default
    });

    const bounds = new window.google.maps.LatLngBounds();
    const infoWindows = [];

    // Cores para diferentes usuários
    const colors = ['red', 'blue', 'green', 'purple', 'orange', 'yellow'];

    trackings.forEach((tracking, userIndex) => {
      if (!tracking.locations || tracking.locations.length === 0) return;

      const color = colors[userIndex % colors.length];
      const path = tracking.locations.map(loc => ({
        lat: loc.lat,
        lng: loc.lng
      }));

      // Desenha linha de trajeto
      const polyline = new window.google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: color,
        strokeOpacity: 0.7,
        strokeWeight: 3,
        map
      });

      // Marcador de entrada
      const startMarker = new window.google.maps.Marker({
        position: tracking.locations[0],
        map,
        title: `${tracking.displayName} - Entrada`,
        icon: `http://maps.google.com/mapfiles/ms/icons/${color}-dot.png`,
        animation: window.google.maps.Animation.DROP
      });

      const startInfo = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; font-size: 12px;">
            <strong>${tracking.displayName}</strong><br/>
            Entrada: ${new Date(tracking.clockInTime?.toDate?.()).toLocaleTimeString()}<br/>
            📍 ${tracking.clockInLocation.lat.toFixed(4)}, ${tracking.clockInLocation.lng.toFixed(4)}
          </div>
        `
      });

      startMarker.addListener('click', () => {
        infoWindows.forEach(iw => iw.close());
        startInfo.open(map, startMarker);
        infoWindows.push(startInfo);
      });

      bounds.extend(tracking.locations[0]);

      // Marcador de saída (se houver)
      if (tracking.clockOutLocation) {
        const endMarker = new window.google.maps.Marker({
          position: tracking.clockOutLocation,
          map,
          title: `${tracking.displayName} - Saída`,
          icon: `http://maps.google.com/mapfiles/ms/icons/${color}-dot.png`,
          label: '◉'
        });

        const endInfo = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; font-size: 12px;">
              <strong>${tracking.displayName}</strong><br/>
              Saída: ${new Date(tracking.clockOutTime?.toDate?.()).toLocaleTimeString()}<br/>
              ⏱️ Duração: ${formatDuration(tracking.totalMinutes)}<br/>
              📍 ${tracking.clockOutLocation.lat.toFixed(4)}, ${tracking.clockOutLocation.lng.toFixed(4)}
            </div>
          `
        });

        endMarker.addListener('click', () => {
          infoWindows.forEach(iw => iw.close());
          endInfo.open(map, endMarker);
          infoWindows.push(endInfo);
        });

        bounds.extend(tracking.clockOutLocation);
      }

      // Marcadores de pontos intermediários
      const intermediatePoints = tracking.locations?.slice(1, -1) || [];
      intermediatePoints.forEach((loc, idx) => {
        if (idx % 5 === 0) { // Mostra a cada 5 pontos para não ficar muito poluído
          const marker = new window.google.maps.Marker({
            position: { lat: loc.lat, lng: loc.lng },
            map,
            title: `Ponto ${idx}`,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 4,
              fillColor: color,
              fillOpacity: 0.4,
              strokeColor: color,
              strokeWeight: 1
            }
          });

          bounds.extend({ lat: loc.lat, lng: loc.lng });
        }
      });
    });

    // Ajusta zoom para mostrar todos os pontos
    if (bounds.isEmpty()) {
      map.setCenter({ lat: -37.78, lng: 174.77 });
      map.setZoom(14);
    } else {
      map.fitBounds(bounds);
    }

  }, [isLoaded, trackings]);

  if (loadError) {
    return (
      <Alert variant="danger">
        Erro ao carregar Google Maps: {loadError.message}
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Carregando mapa...</p>
      </div>
    );
  }

  return <div ref={mapRef} style={mapContainerStyle} />;
}
